import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, ArrowRight, KeyRound, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';
import PublicLayout from '../components/public/PublicLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code+new password, 3: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const sendCode = async (e) => {
    e?.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/ebn/auth/forgot-password`, { email });
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send reset code. Please try again.');
    }
    setLoading(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/ebn/auth/reset-password`, { email, code, new_password: newPassword });
      setStep(3);
      setTimeout(() => navigate('/login'), 3500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reset password. Please try again.');
    }
    setLoading(false);
  };

  return (
    <PublicLayout>
      <section className="py-12 sm:py-16 bg-gradient-to-br from-[#F5A623]/5 to-white">
        <div className="max-w-md mx-auto px-4">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2A4F] hover:text-[#F5A623] mb-6" data-testid="forgot-back-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-7 sm:p-9">
            {step === 1 && (
              <>
                <div className="w-14 h-14 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center mx-auto">
                  <KeyRound className="w-7 h-7" />
                </div>
                <h1 className="mt-4 text-2xl font-bold text-center text-[#1A2A4F]">Forgot your password?</h1>
                <p className="mt-2 text-sm text-center text-gray-500">No worries — enter your email and we'll send you a 6-digit reset code.</p>
                <form onSubmit={sendCode} className="mt-6 space-y-4" data-testid="forgot-step1-form">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Email address</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-3 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  {error && <p className="text-sm text-red-600" data-testid="forgot-error-1">{error}</p>}
                  <button type="submit" disabled={loading || !email} className="w-full bg-[#F5A623] text-white font-bold py-3 rounded-lg hover:brightness-95 disabled:opacity-60 transition flex items-center justify-center gap-2" data-testid="forgot-send-btn">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send reset code <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </>
            )}

            {step === 2 && (
              <>
                <div className="w-14 h-14 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center mx-auto">
                  <Mail className="w-7 h-7" />
                </div>
                <h1 className="mt-4 text-2xl font-bold text-center text-[#1A2A4F]">Check your email</h1>
                <p className="mt-2 text-sm text-center text-gray-500">If an account exists for <strong>{email}</strong>, we've sent a 6-digit code.</p>
                <form onSubmit={submitReset} className="mt-6 space-y-4" data-testid="forgot-step2-form">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">6-digit code</label>
                    <input
                      type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required
                      value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="w-full border border-gray-200 rounded-lg px-3 py-3 text-center text-lg tracking-[10px] font-bold text-[#1A2A4F] focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                      data-testid="forgot-code-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">New password</label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type={showPw ? 'text' : 'password'} required minLength={6}
                        value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 6 characters"
                        className="w-full border border-gray-200 rounded-lg pl-10 pr-12 py-3 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                        data-testid="forgot-newpw-input"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1A2A4F]" data-testid="forgot-pw-toggle">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {error && <p className="text-sm text-red-600" data-testid="forgot-error-2">{error}</p>}
                  <button type="submit" disabled={loading || code.length !== 6 || newPassword.length < 6} className="w-full bg-[#F5A623] text-white font-bold py-3 rounded-lg hover:brightness-95 disabled:opacity-60 transition flex items-center justify-center gap-2" data-testid="forgot-reset-btn">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Resetting…</> : 'Reset password'}
                  </button>
                  <button type="button" onClick={sendCode} disabled={loading} className="w-full text-sm font-semibold text-[#F5A623] hover:underline" data-testid="forgot-resend-btn">Resend code</button>
                </form>
              </>
            )}

            {step === 3 && (
              <div className="text-center py-6" data-testid="forgot-success">
                <CheckCircle2 className="w-14 h-14 text-[#F5A623] mx-auto" />
                <h1 className="mt-4 text-2xl font-bold text-[#1A2A4F]">Password reset!</h1>
                <p className="mt-2 text-sm text-gray-500">You'll be redirected to sign in in a moment…</p>
                <Link to="/login" className="mt-5 inline-block bg-[#F5A623] text-white font-bold px-6 py-3 rounded-lg" data-testid="forgot-goto-login">
                  Sign in now
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ForgotPasswordPage;
